/**
 * Data hooks for the Operations Intake Verification surface.
 *
 *   useIntakeQueue()  — polls GET /api/intake-verification every 15s.
 *   useVerifyGroup()  — POSTs a group's verification; optimistically
 *                       removes it from the cached queue so the row
 *                       disappears the instant you click, rolling back
 *                       if the write fails.
 *
 * 15-second polling matches the rest of the app's posture (no
 * websockets in v1). The queue is small (today's unverified leads), so
 * refetching the whole list is cheap.
 *
 * Spec: engineering/command-center/docs/port-notes/intake-verification.md (evicted 2026-08-29, #775 round 10)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IntakeQueueResponseSchema,
  type IntakeQueueResponse,
} from "@command-center/shared";
import { apiGet, apiPost } from "./api";

const QUEUE_KEY = ["intake-verification"] as const;
const POLL_INTERVAL_MS = 15_000;

export function useIntakeQueue() {
  return useQuery<IntakeQueueResponse, Error>({
    queryKey: QUEUE_KEY,
    queryFn: async () => {
      const raw = await apiGet<unknown>("/api/intake-verification");
      const parsed = IntakeQueueResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/intake-verification failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    refetchInterval: POLL_INTERVAL_MS,
  });
}

export function useVerifyGroup() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, Error, string, { previous?: IntakeQueueResponse }>({
    mutationFn: (groupId: string) =>
      apiPost<{ ok: true }>(
        `/api/intake-verification/${encodeURIComponent(groupId)}/verify`,
        {},
      ),
    // Optimistically drop the verified group from the cached queue so
    // the row vanishes immediately.
    onMutate: async (groupId) => {
      await queryClient.cancelQueries({ queryKey: QUEUE_KEY });
      const previous =
        queryClient.getQueryData<IntakeQueueResponse>(QUEUE_KEY);
      if (previous) {
        queryClient.setQueryData<IntakeQueueResponse>(QUEUE_KEY, {
          groups: previous.groups.filter((g) => g.id !== groupId),
        });
      }
      return { previous };
    },
    // Roll back on failure — put the group back so the operator sees it
    // wasn't actually verified.
    onError: (_err, _groupId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(QUEUE_KEY, context.previous);
      }
    },
    // Re-sync with the server either way (a poll may have changed the
    // queue while the request was in flight).
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
  });
}
